import { useCallback, useState } from "react";
import useSWR from "swr";

import {
  createRecipe,
  deleteRecipe,
  listRecipes,
  RECIPES_KEY,
  updateRecipe,
  type CreateRecipePayload,
  type UpdateRecipePayload,
} from "@/features/recipes/api/recipesApi";
import type { Recipe } from "@/features/recipes/types";
import { revalidate, toErrorMessage } from "@/lib/swr";

const EMPTY: Recipe[] = [];

/** A recipe drives production, so both stocks move with it. */
const TOUCHED_CACHES = [RECIPES_KEY, "products", "product-stocks/", "provision-stocks/"];

export function useRecipes() {
  const { data, error, isLoading, mutate } = useSWR(RECIPES_KEY, listRecipes);

  return {
    recipes: data ?? EMPTY,
    isLoading,
    error: toErrorMessage(error),
    refresh: mutate,
  };
}

export function useCreateRecipe() {
  const { mutate } = useSWR(RECIPES_KEY, listRecipes);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = useCallback(
    async (payload: CreateRecipePayload) => {
      setIsCreating(true);
      setError(null);
      try {
        const created = await createRecipe(payload);
        await mutate();
        return created;
      } catch (e) {
        setError(toErrorMessage(e));
        throw e;
      } finally {
        setIsCreating(false);
      }
    },
    [mutate],
  );

  return { create, isCreating, error };
}

export function useUpdateRecipe() {
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = useCallback(async (id: string, payload: UpdateRecipePayload) => {
    setIsUpdating(true);
    setError(null);
    try {
      const updated = await updateRecipe(id, payload);
      await revalidate(...TOUCHED_CACHES);
      return updated;
    } catch (e) {
      setError(toErrorMessage(e));
      throw e;
    } finally {
      setIsUpdating(false);
    }
  }, []);

  return { update, isUpdating, error };
}

export function useDeleteRecipe() {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remove = useCallback(async (id: string) => {
    setIsDeleting(true);
    setError(null);
    try {
      const deleted = await deleteRecipe(id);
      await revalidate(...TOUCHED_CACHES);
      return deleted;
    } catch (e) {
      setError(toErrorMessage(e));
      throw e;
    } finally {
      setIsDeleting(false);
    }
  }, []);

  return { remove, isDeleting, error };
}
